import { Attribute, AttributeViewType } from '@/api/AttributeApi';
import { AttributeValue } from '@/api/AttributeValueApi';
import { Component } from '@/api/ComponentApi';
import { RootState } from '@/store';
import { creatorActions } from '@/store/slices/creatorSlice';
import {
	CreatorState,
	CreatorOptions,
	CreatorAccessoryCategory,
	CreatorAccessory,
	CreatorStepType,
} from '@/types/creator';
import { ListenerEffect } from '@reduxjs/toolkit';
import {
	ComponentRelation,
	ComponentRelationAction,
	ComponentRelationOperator,
	ComponentRelationType,
	RelationConditionOperator,
	UUID,
} from '@/types/types';
import { ProductType, ProductTypeTemplate } from '@/api/ProductTypeApi';
import { Accessory } from '@/api/AccessoryApi';
import { isAttributeAnswered } from './utilities';
import ProductApi, { Product, ProductAttribute } from '@/api/ProductApi';

export function getInitialAttributes(components: Component[]) {
	const values: CreatorState['availableAttributes'] = {};

	for (const component of components) {
		values[component.id] = component.componentAttributes.map((attribute) => ({
			...attribute,
			componentAttributeValues: attribute.componentAttributeValues.map(
				(attributeValue) => ({
					...attributeValue,
					disabled: false,
				})
			),
			disabled: false,
		}));
	}

	return values;
}

export function getInitialAccessories(accessories: Accessory[]) {
	const creatorAccessorycategories: CreatorState['availableAccessories'] =
		accessories.reduce<CreatorAccessoryCategory[]>((acc, accessory) => {
			const category = acc.find(
				(category) => category.id === accessory.category.id
			);

			const creatorAccessory: CreatorAccessory = {
				...accessory,
				disabled: false,
			};

			if (!category)
				acc.push({ ...accessory.category, accessories: [creatorAccessory] });
			else category.accessories.push(creatorAccessory);

			return acc;
		}, []);

	return creatorAccessorycategories;
}

export function setLoadedChosenAccessories(accessories: Accessory[]) {
	const chosenAccessories: CreatorState['chosenAccessories'] = {};

	for (const accessory of accessories) {
		chosenAccessories[accessory.category.id] = accessory;
	}

	return chosenAccessories;
}

export function getInitialChosenValues(
	components: Component[],
	productAttributes: ProductAttribute[]
) {
	const values: CreatorState['chosenProductAttributes'] = {};

	for (const component of components) {
		values[component.id] = component.componentAttributes.reduce<
			Record<UUID, ProductAttribute>
		>((acc, curr) => {
			const chosenAttribute = productAttributes.find(
				(attr) => attr.attribute.id === curr.attribute.id
			);

			acc[curr.attribute.id] = chosenAttribute || {
				componentAttributeValue: null,
				attribute: curr.attribute,
				value: '',
			};
			return acc;
		}, {});
	}

	return values;
}

export function displayAttributeValueName(
	attributeValue: Omit<AttributeValue, 'attribute'>,
	attribute: Attribute
) {
	const name =
		attributeValue.displayName ||
		`${attributeValue.value} ${attribute.suffix || ''}`.trim();

	return name;
}

export function getCreatorSteps(productType: ProductType) {
	const { components } = productType;

	const steps: CreatorOptions['availableSteps'] = [];

	if (productType.template === ProductTypeTemplate.ADVANCED)
		steps.push({
			order: 0,
			label: 'Wymiary',
			disabled: false,
			type: CreatorStepType.DIMENSIONS,
		});

	for (let index = 0; index < components.length; index++) {
		const component = components[index];

		steps.push({
			order: 0,
			label: component.displayName || component.name,
			componentId: component.id,
			disabled: false,
			type: CreatorStepType.COMPONENTS,
		});
	}

	if (productType.template === ProductTypeTemplate.ADVANCED)
		steps.push({
			order: 0,
			label: 'Akcesoria',
			disabled: false,
			type: CreatorStepType.ACCESSORIES,
		});

	steps.push({
		order: 0,
		label: 'Podsumowanie',
		disabled: false,
		type: CreatorStepType.SUMMARY,
	});

	steps.forEach((step, idx) => {
		step.order = idx;
	});

	return steps;
}

export function filterAttributes(
	product: Product,
	chosenProductAttributes: CreatorState['chosenProductAttributes'],
	attributesWithAvailability: CreatorState['availableAttributes']
) {
	const productAttributes = Object.values(chosenProductAttributes).reduce<
		ProductAttribute[]
	>((acc, curr) => [...acc, ...Object.values(curr)], []);

	for (const componentAttribute of Object.values(
		attributesWithAvailability
	).flat()) {
		if (componentAttribute.attribute.viewType !== AttributeViewType.TILES) {
			let disabled = false;

			if (componentAttribute.relations.length) {
				for (const relation of componentAttribute.relations) {
					const fullfiled = isRelationFulfilled(
						relation,
						product,
						productAttributes
					);

					if (relation.action === ComponentRelationAction.HIDE)
						disabled = fullfiled;
					else disabled = !fullfiled;
				}
			}

			componentAttribute.disabled = disabled;
			continue;
		}

		for (const componentAttributeValue of componentAttribute.componentAttributeValues) {
			if (!componentAttributeValue.relations?.length) {
				componentAttributeValue.disabled = false;
				continue;
			}

			const relationsValidation: boolean[] = [];

			for (const relation of componentAttributeValue.relations) {
				const fullfiled = isRelationFulfilled(
					relation,
					product,
					productAttributes
				);

				relationsValidation.push(
					relation.action === ComponentRelationAction.HIDE
						? !fullfiled
						: fullfiled
				);
			}

			componentAttributeValue.disabled = relationsValidation.some(
				(check) => !check
			);
		}

		if (!componentAttribute.relations?.length) {
			componentAttribute.disabled =
				componentAttribute.componentAttributeValues.every(
					(value) => value.disabled
				);
			continue;
		} else {
			const relationsValidation: boolean[] = [];

			for (const relation of componentAttribute.relations) {
				const fullfiled = isRelationFulfilled(
					relation,
					product,
					productAttributes
				);

				relationsValidation.push(
					relation.action === ComponentRelationAction.HIDE
						? !fullfiled
						: fullfiled
				);
			}

			componentAttribute.disabled = relationsValidation.some((check) => !check);
		}
	}
}

export function filterAccessories(
	product: Product,
	chosenProductAttributes: CreatorState['chosenProductAttributes'],
	availableAccessories: CreatorState['availableAccessories']
) {
	const accessories = availableAccessories.reduce<CreatorAccessory[]>(
		(acc, curr) => [...acc, ...curr.accessories],
		[]
	);

	const productAttributes = Object.values(chosenProductAttributes).reduce<
		ProductAttribute[]
	>((acc, curr) => [...acc, ...Object.values(curr)], []);

	for (const accessory of accessories) {
		if (!accessory.relations?.length) {
			accessory.disabled = false;
			continue;
		}

		let disabled = false;

		for (const relation of accessory.relations) {
			const fullfiled = isRelationFulfilled(
				relation,
				product,
				productAttributes
			);

			if (relation.action === ComponentRelationAction.HIDE)
				disabled = fullfiled;
			else disabled = !fullfiled;
		}

		accessory.disabled = disabled;
	}
}

function isRelationFulfilled(
	relation: ComponentRelation,
	product: Product,
	productAttributes: ProductAttribute[]
) {
	let fullfiled = false;

	for (const condition of relation.conditions) {
		switch (condition.type) {
			case ComponentRelationType.ATTRIBUTE_VALUE:
				fullfiled = productAttributes.some(
					(productAttribute) =>
						productAttribute.componentAttributeValue?.attributeValue.id ===
						condition.objectId
				);
				break;

			case ComponentRelationType.PRODUCT_PROPERTY:
				const chosenValue = product[condition.property],
					targetValue = Number(condition.propertyValue);

				switch (condition.propertyOperator) {
					case RelationConditionOperator.LTE:
						fullfiled = chosenValue <= targetValue;
						break;
					case RelationConditionOperator.GTE:
						fullfiled = chosenValue >= targetValue;
						break;
					case RelationConditionOperator.EQ:
						fullfiled = chosenValue === targetValue;
						break;
					default:
						throw new Error('UNKNOWN_CONDITION_PROPERTY_OPERATOR');
				}
				break;

			case ComponentRelationType.ATTRIBUTE:
				fullfiled = productAttributes.some(
					(productAttribute) =>
						productAttribute.attribute.id === condition.objectId &&
						(productAttribute.componentAttributeValue || productAttribute.value)
				);
				break;

			default:
				throw new Error('UNKNOWN_RELATION_TYPE');
		}

		if (relation.operator === ComponentRelationOperator.AND && !fullfiled)
			break;
		if (relation.operator === ComponentRelationOperator.OR && fullfiled) break;
	}

	return fullfiled;
}

export function filterSteps(
	steps: CreatorOptions['availableSteps'],
	availableAttributes: CreatorState['availableAttributes']
) {
	for (const step of steps) {
		if (!step.componentId) continue;

		step.disabled = availableAttributes[step.componentId].every(
			(componentAttribute) => componentAttribute.disabled
		);
	}
}

export function filterComponents(
	availableAttributes: CreatorState['availableAttributes']
) {
	return (component: Component) =>
		availableAttributes[component.id].some(
			(componentAttribute) => !componentAttribute.disabled
		);
}

export function unselectDisabledOptions(
	chosenProductAttributes: CreatorState['chosenProductAttributes'],
	availableAttributes: CreatorState['availableAttributes'],
	chosenAccessories: CreatorState['chosenAccessories'],
	availableAccessories: CreatorState['availableAccessories']
) {
	let changed = false;

	for (const [componentId, attributeRecord] of Object.entries(
		chosenProductAttributes
	)) {
		for (const [attributeId, productAttribute] of Object.entries(
			attributeRecord
		)) {
			if (!productAttribute.componentAttributeValue) continue;

			const availableAttributeValue = availableAttributes[componentId as UUID]
				.find((attribute) => attribute.attribute.id === attributeId)
				?.componentAttributeValues.find(
					(value) => value.id === productAttribute.componentAttributeValue!.id
				);

			if (!availableAttributeValue) continue;

			if (availableAttributeValue.disabled) {
				productAttribute.componentAttributeValue = null;
				changed = true;
			}
		}
	}

	for (const [acessoryCategoryId, selectedAcessory] of Object.entries(
		chosenAccessories
	)) {
		const availableAccessory = availableAccessories
			.find((accessoryCategory) => accessoryCategory.id === acessoryCategoryId)
			?.accessories.find((accessory) => accessory.id === selectedAcessory.id);

		if (!availableAccessory) continue;

		if (availableAccessory.disabled) {
			delete chosenAccessories[acessoryCategoryId as UUID];
			changed = true;
		}
	}

	return changed;
}

export async function handleStateFiltering({
	product,
	chosenProductAttributes,
	availableAttributes,
	chosenAccessories,
	availableAccessories,
	options,
}: CreatorState) {
	do {
		filterAttributes(product!, chosenProductAttributes, availableAttributes);
		filterAccessories(product!, chosenProductAttributes, availableAccessories);
		filterSteps(options.availableSteps, availableAttributes);
	} while (
		unselectDisabledOptions(
			chosenProductAttributes,
			availableAttributes,
			chosenAccessories,
			availableAccessories
		)
	);
}

export const syncEffect: ListenerEffect<any, RootState, any> = async (
	_,
	listenerApi
) => {
	const { product, chosenProductAttributes, clientId } =
		listenerApi.getState().creator;
	if (!product) return;

	const productAttributes = Object.values(chosenProductAttributes).reduce<
		ProductAttribute[]
	>((acc, attributeAnswers) => {
		for (const [attributeId, answer] of Object.entries(attributeAnswers)) {
			acc.push({
				attribute: { id: attributeId } as Attribute,
				componentAttributeValue: answer.componentAttributeValue,
				value: answer.value,
			});
		}
		return acc;
	}, []);

	listenerApi.cancelActiveListeners();
	await listenerApi.delay(1000);

	const oldProps = [product.surface, product.weight];

	const {
		price,
		netPrice,
		discountedPrice,
		discountedNetPrice,
		surface,
		weight,
	} = await ProductApi.update({
		id: product.id,
		productAttributes,
		clientId,
	});

	listenerApi.dispatch(
		creatorActions.updateProduct({
			price,
			netPrice,
			discountedPrice,
			discountedNetPrice,
			surface,
			weight,
		})
	);

	const hasChanged = oldProps.some(
		(prop, index) => prop !== [surface, weight][index]
	);

	if (hasChanged) listenerApi.dispatch(creatorActions.filterAttributes());
};

export function isCreatorValid(state: CreatorState) {
	const requiredAttributes = Object.values(state.availableAttributes)
		.flat()
		.filter((attr) => {
			if (!attr.required) return false;
			if (attr.disabled) return false;

			return true;
		});

	const chosen = Object.values(state.chosenProductAttributes).reduce(
		(acc, curr) => {
			Object.assign(acc, curr);
			return acc;
		},
		{}
	);

	for (const requiredAttr of requiredAttributes) {
		const productAttribute = chosen[requiredAttr.attribute.id];

		if (!isAttributeAnswered(productAttribute)) return false;
	}

	return true;
}
